<template>
  <div v-if="!isLoading && isSuccess && data" class="automation-card">
    <div class="header">
      <v-icon>mdi-lightning-bolt</v-icon>
      <vs-heading>Automation</vs-heading>
      <v-tooltip v-if="mostRecentReading" bottom>
        <template #activator="{ on, attrs }">
          <span v-bind="attrs" class="reading-timestamp" data-test="most-recent-reading" v-on="on">
            Updated {{ mostRecentReadingString }}
          </span>
        </template>
        <span>{{ mostRecentReadingExact }}</span>
      </v-tooltip>
    </div>

    <div class="metrics-panel">
      <div class="metric">
        <span class="metric-label">Fill</span>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" data-test="tank-fill-percentage" class="metric-value" v-on="on">
              {{ tankFillPercentage }}
            </span>
          </template>
          <span data-test="tank-fill-volume">{{ tankFillString }}</span>
        </v-tooltip>
      </div>
      <div class="metric">
        <span class="metric-label">Flow rate</span>
        <span class="metric-value" data-test="tank-flow-rate">{{ flowRateDisplay }}</span>
      </div>
      <div class="metric">
        <span class="metric-label">Full in</span>
        <span class="metric-value" data-test="tank-eta-full">{{ etaFullString }}</span>
      </div>
    </div>

    <div v-if="data.sensorIds && data.sensorIds.length > 0" class="metrics-panel">
      <sensor-readings-chart
        class="sensor-chart"
        :sensor-ids="data.sensorIds"
        :organization-id="data.organizationId"
        :window-start="new Date(new Date().getTime() - 24 * 60 * 60 * 1000)"
        :window-end="new Date()"
        :capacity="data.capacity || 0"
      />
    </div>

    <JobPredictionLinks
      :trigger-organization-id="data.organizationId"
      :trigger-url="data.triggerConfigurationLink"
      :related-job-numbers="data.relatedJobNumbers"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, unref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { useGetJobAutomationQuery } from '@/api/jobs-prediction'
import SensorReadingsChart from '@/components/jobs/SensorReadingsChart.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import JobPredictionLinks from './JobPredictionLinks.vue'
import moment from 'moment'

interface Props {
  jobNumber: number
}

const props = defineProps<Props>()
const store = useStore()
const scadaUnits = computed(() => {
  return store.state.organizationConfig.scadaUnits
})

const POLL_INTERVAL_MS = 60_000

const { data, isLoading, isSuccess } = useGetJobAutomationQuery(
  computed(() => props.jobNumber),
  { refetchInterval: POLL_INTERVAL_MS },
)

const tankFillPercentage = computed(() => {
  const computedData = unref(data)

  if (!computedData || computedData?.percentFull === null) {
    return '-'
  }

  return `${computedData.percentFull.toFixed(2)}%`
})

const tankFillString = computed(() => {
  const computedData = unref(data)

  if (!computedData || computedData?.currentLevel === null || computedData?.capacity === null) {
    return '-'
  }

  return `${computedData.currentLevel.toFixed(2)} / ${computedData.capacity.toFixed(2)}`
})

const flowRateString = computed(() => {
  const computedData = unref(data)

  if (!computedData || computedData?.flowRate === null) {
    return '-'
  }

  const dailyFlowRate = computedData.flowRate * 24
  return `${dailyFlowRate.toFixed(2)}`
})

const flowRateDisplay = computed(() => {
  const value = flowRateString.value
  return value === '-' ? '-' : `${value} ${scadaUnits.value}/day`
})

const mostRecentReading = computed(() => {
  const computedData = unref(data)

  if (!computedData || !computedData?.mostRecentReadingReceivedAt) {
    return null
  }

  return moment.utc(computedData.mostRecentReadingReceivedAt).local()
})

const mostRecentReadingString = computed(() => {
  return mostRecentReading.value?.fromNow()
})

const mostRecentReadingExact = computed(() => {
  return mostRecentReading.value?.format('MMMM Do, YYYY [at] h:mm:ss A')
})

const etaFullString = computed(() => {
  const computedData = unref(data)

  if (!computedData || computedData?.minutesUntilFull === null) {
    return '-'
  }

  const duration = moment.duration(computedData.minutesUntilFull, 'minutes')

  if (duration.asDays() >= 1) {
    return Math.floor(duration.asDays()) + ' days'
  } else if (duration.asHours() >= 1) {
    return Math.floor(duration.asHours()) + ' hrs'
  } else if (duration.asMinutes() >= 1) {
    return Math.floor(duration.asMinutes()) + ' mins'
  } else {
    return Math.floor(duration.asSeconds()) + ' secs'
  }
})
</script>

<style scoped>
.automation-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 16px;
  font-family: var(--font-regular);
  background: linear-gradient(135deg, #dfebfd 0%, #e8f1fe 60%, #edf4fe 100%);
  border: 1px solid #cde0fb;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header .v-icon {
  color: var(--color-blue);
}

.metrics-panel {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(6, 95, 212, 0.1);
  gap: 12px 24px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.sensor-chart {
  flex: 1 1 100%;
  min-width: 0;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reading-timestamp {
  padding-top: 2px;
  font-size: 12px;
  color: var(--color-grey);
}

.metric-label {
  font-size: 14px;
  color: var(--color-grey);
}

.metric-value {
  font-size: 15px;
  font-weight: 700;
}
</style>
